"use client";

import { useLocale } from "next-intl";
import { useRouter, usePathname } from "@/i18n/routing";
import { useState, useRef, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Globe, ChevronDown, Check } from "lucide-react";
import Image from "next/image";
import { cn } from "@/lib/utils";

const languages = [
  { code: "pl", label: "Polski", flag: "pl" },
  { code: "en", label: "English", flag: "gb" },
  { code: "de", label: "Deutsch", flag: "de" },
  { code: "nl", label: "Nederlands", flag: "nl" },
];

const DROPDOWN_WIDTH = 192;

export function LanguageSwitcher() {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownSide, setDropdownSide] = useState<"start" | "end">("end");
  const triggerRef = useRef<HTMLDivElement>(null);

  const switchLocale = useCallback(
    (newLocale: string) => {
      router.replace(pathname, { locale: newLocale });
      router.refresh();
      setIsOpen(false);
    },
    [router, pathname],
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleToggle = useCallback(() => {
    if (!isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const fitsOnRight = rect.left + DROPDOWN_WIDTH <= window.innerWidth;
      const fitsOnLeft = rect.right - DROPDOWN_WIDTH >= 0;

      if (fitsOnLeft) {
        setDropdownSide("end");
      } else if (fitsOnRight) {
        setDropdownSide("start");
      } else {
        const spaceLeft = rect.right;
        const spaceRight = window.innerWidth - rect.left;
        setDropdownSide(spaceLeft >= spaceRight ? "end" : "start");
      }
    }
    setIsOpen((v) => !v);
  }, [isOpen]);

  return (
    <div className="relative z-50" ref={triggerRef}>
      <button
        onClick={handleToggle}
        className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors cursor-pointer pointer-events-auto"
        aria-label="Select Language"
        aria-expanded={isOpen}
      >
        <Globe className="w-5 h-5 text-(--brand-primary)" />
        <span className="hidden md:block text-sm font-medium uppercase">
          {locale}
        </span>
        <ChevronDown
          className={cn(
            "w-4 h-4 transition-transform duration-200",
            isOpen && "rotate-180",
          )}
        />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.96 }}
            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              "absolute top-full mt-2 w-48 bg-(--bg-card) border border-(--border-subtle) rounded-xl shadow-xl overflow-hidden py-1",
              dropdownSide === "start" ? "left-0" : "right-0",
            )}
          >
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => switchLocale(lang.code)}
                className={cn(
                  "w-full px-4 py-3 text-left flex items-center justify-between text-sm transition-colors hover:bg-black/5 dark:hover:bg-white/5",
                  locale === lang.code && "text-(--brand-primary) font-medium",
                )}
              >
                <div className="flex items-center gap-3">
                  <Image
                    src={`/images/flags/${lang.flag}.svg`}
                    alt={lang.label}
                    width={18}
                    height={18}
                  />
                  <span>{lang.label}</span>
                </div>
                {locale === lang.code && <Check className="w-4 h-4" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
